<template>
  <custom-fields-list
    v-if="customFieldsExist"
    :fields="customFields"
    :item-class="fieldClasses"
    :list-class="containerClasses"
    :military-time-enabled="$isMilitaryTimeEnabled(warehouse)"
    :timezone="timezone ?? warehouse?.timezone"
    :warehouse="warehouse"
    @toggle-confirm-button="uploading => $emit('toggle-confirm-button', uploading)" />
</template>

<script>
import { updateCustomFieldsFromWarehouse } from '@nova/core';
import CustomFieldsList from '@satellite/components/custom_forms/CustomFieldsList.vue';
import { provideCustomFormsStorageContext } from '@satellite/composables/useCustomFormsStorageContext';

/**
 * The legacy custom fields: the template configured on the warehouse, whose fields carry their
 * value inline and are persisted as `appointment.customFields`.
 *
 * Replaces a pair of near-identical components — this one and CustomFieldsOld — that differed
 * only in which family of field components they resolved. Once both pointed at satellite they
 * were the same component twice, so the props here are the union of what the call sites of each
 * were already passing.
 *
 * @displayName Custom Fields
 */
export default {
  name: 'CustomFields',
  components: { CustomFieldsList },
  emits: ['update:model-value', 'toggle-confirm-button'],
  props: {
    /** Warehouse entity providing the custom fields template */
    warehouse: { type: Object, default: null },
    appointment: {
      type: Object,
      required: false,
      default() {
        return {};
      }
    },
    initialCustomFields: { type: Array, required: false, default: null },
    timezone: { type: String, required: false, default: undefined },
    isInline: { type: Boolean, default: true },
    containerClasses: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    fieldClasses: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    /** @deprecated Accepted for compatibility; no field type reads it. */
    singleLine: { type: Boolean, default: false }
  },
  setup(props) {
    /** A warehouse-configured document field can render here, and injects this to upload. */
    provideCustomFormsStorageContext(() => props.warehouse?.id);
  },
  data() {
    return {
      customFields: []
    };
  },
  computed: {
    customFieldsExist() {
      return this.customFields?.length;
    },
    existingCustomFields() {
      return this.initialCustomFields?.length
        ? this.initialCustomFields
        : this.appointment?.customFields;
    }
  },
  methods: {
    setCustomFields() {
      this.customFields = updateCustomFieldsFromWarehouse(
        this.existingCustomFields,
        this.warehouse,
        false
      );
    }
  },
  mounted() {
    this.setCustomFields();
  },
  watch: {
    warehouse() {
      this.setCustomFields();
    },
    customFields() {
      /**
       * Emits the whole field list, which is the shape the parents bind with v-model.
       * @event update:model-value
       */
      this.$emit('update:model-value', this.customFields);
    }
  }
};
</script>
